import { t } from 'i18next';

export type SmsType = 'MEMBER_CREATION' | 'MEMBER_PASSWORD_RESET';

export type GenderType = 'MALE' | 'FEMALE';

export type SupplyType = 'PURCHASING' | 'WAREHOUSE';

export type ConfigurationType = 'NONE' | 'CONFIGURABLE';

export type OrderStateType =
  | 'COMPLETELY_PROCESSED'
  | 'NOT_YET_PROCESSED'
  | 'PARTIALLY_PROCESSED';

export type OrderInvoiceStateType =
  | 'COMPLETELY_PROCESSED'
  | 'NOT_YET_PROCESSED'
  | 'PARTIALLY_PROCESSED';

export type InvoiceType =
  | 'DELIVERYRELATION'
  | 'NORELATIONSHIP'
  | 'ORDERRELATION';

export type PriceType = 'NOPRICING' | 'STANDARTPRICING';

export type RoundingType = 'FLOOR' | 'CEIL' | 'ROUND';

export type ApplyOnType = 'ORDER_ITEM' | 'ORDER';

export type UnitType = 'AMOUNT' | 'PERCENT';

export type ConditionClassType = 'PRICE' | 'DISCOUNT' | 'TAX';

export type CamapignConjunctionType = 'AND' | 'OR';

export type IGeographicBoundaryResult = {
  id: number;
  name: string;
  description: string;
  code: string;
  type: {
    code: string;
    description: string;
    id: number;
    name: string;
    status: boolean;
  };
};

export type IAccountResult = {
  id?: number;
  activated?: boolean;
  email?: string;
  phoneNumber: string;
  langKey?: string;
  login?: string;
  individual?: IIndividualResult;
  permissions?: string[];
  citizenshipNumber?: string;
  citizenshipCountryId?: number;
  membershipContractApproved?: boolean;
  employee?: boolean;
};

export type IAccountInput = {
  activated?: boolean;
  email?: string;
  phoneNumber?: string;
  langKey?: string;
  login?: string;
  individual?: IIndividualInput;
  permission?: string[];
  citizenshipNumber?: string;
  citizenshipCountryId?: number;
  membershipContractApproved?: boolean;
};

export type IIndividualResult = {
  id: number;
  alcoholUser?: boolean;
  birthCountry: IGeographicBoundaryResult;
  birthDate: string;
  birthPlace: string;
  bloodType?: IReferenceCode;
  bodySize?: string;
  bodyHeight?: number;
  bodyWeight?: number;
  disability?: IReferenceCode;
  militaryStatus?: IReferenceCode;
  occupation?: IReferenceCode;
  education?: IReferenceCode;
  fatherName?: string;
  firstName: string;
  footSize?: string;
  gender: GenderType;
  jobInfo?: string;
  lastName: string;
  maidenName?: string;
  mezheb?: IReferenceCode;
  motherName?: string;
  quranReader?: boolean;
  smokeUser?: boolean;
  customerNumber?: number;
};

export type IIndividualInput = {
  alcoholUser?: boolean;
  birthCountry?: IGeographicBoundaryResult;
  birthDate?: string;
  birthPlace?: string;
  bloodType?: IReferenceCode;
  bodySize?: string;
  bodyHeight?: number;
  bodyWeight?: number;
  disability?: IReferenceCode;
  militaryStatus?: IReferenceCode;
  occupation?: IReferenceCode;
  education?: IReferenceCode;
  fatherName?: string;
  firstName?: string;
  footSize?: string;
  gender?: GenderType;
  jobInfo?: string;
  lastName?: string;
  maidenName?: string;
  mezheb?: IReferenceCode;
  motherName?: string;
  quranReader?: boolean;
  smokeUser?: boolean;
  customerNumber?: number;
};

export type IReferenceCode = {
  id?: number;
  name?: string;
  code?: string;
  description?: string;
  status?: boolean;
  orderPriority?: number;
  typeId?: number;
  parent?: IReferenceCodeBaseLOV;
};

export type IReferenceCodeBaseLOV = {
  code?: string;
};

export type PointType =
  | 'DEBIT'
  | 'DEBITREFUND'
  | 'CREDIT'
  | 'CREDITREFUND'
  | 'TRANSFER'
  | 'TRANSFERREFUND';

export type PointStatus = 'ACTIVE' | 'BLOCKED';

export type IExternalContactMedium = {
  description: string;
  electronicAddressInput: {
    electronicAddress: string;
    typeId: number;
  };
  fromDate: string;
  owner: string;
  phoneNumberInput: {
    countryCode: string;
    phoneNumber: string;
    typeId: number;
  };
  postalAddressInput: {
    doorNumber: string;
    geographicBoundaryId: number;
    postCode: string;
    streetAddress1: string;
    typeId: number;
  };
  toDate: string;
};

export type IPointLoyalty = {
  points: number;
  fiat: number;
  currency?: {
    id: number;
    code: string;
    name: string;
  };
  currencyExchangeRate?: number;
};

export type ILoyaltyPrograms = {
  id: number;
  company: {
    id: number;
    name: string;
  };
  membershipNumber: string;
};

export type ILoyaltyCompanies = {
  id: number;
  name: string;
};

export type IIndividualPointResult = {
  id: number;
  loyaltyCompany: string;
  material?: string;
  orderItemNumber?: string;
  orderNumber?: string;
  customer?: string;
  point: number;
  pointType?: PointType;
  startDate?: string;
  status?: PointStatus;
  transactionDate?: string;
  endDate?: string;
  description?: string;
};

export type ICalculatedIndividualPoints = {
  totalPoint: number;
  debitedPoint: number;
  remainingPoint: number;
  activePoint: number;
  blockedPoint: number;
};

export type ISalesOrderItemResult = {
  id: number;
  itemNumber: number;
  taxRate: number; // Ex: 0.18
  parent?: ISalesOrderItemResult;
  quantity: number;
  material: IMaterialResult;
  productionSite: IOrganizationResult;
  warehouse: IOrganizationResult;
  createdBy: User;
  createdDate: string;
  lastModifiedBy: string;
  lastModifiedDate: string;
  pricing: IPricingConditionResult; // Copy of pricing condition
  discounts: IPricingConditionResult[]; // Copies of pricing condition
  total: number; // calculated including discounts
  tour?: ITourResult;
  startDate: Date;
  endDate: Date;
  deliveryDate: Date;
  state: OrderStateType;
  cancelReason: ISimpleModelResult;
  plannedInvoiceDate: Date;
  invoiceState?: OrderInvoiceStateType;
  documentItemType?: IDocumentItemTypeResult;
  condition?: IPricingConditionResult;
  description?: string;
};

export type IPricingConditionResult = {
  id: number;
  sequenceItem: IPricingAccessSequenceItemResult;
  conditionType: IPricingConditionTypeResult;
  amount: number;
  currency?: IReferenceCode;
  startDate: Date;
  endDate: Date;
  termStartDate: Date;
  termEndDate: Date;
  campaign: ICampaignResult;
  salesOrganization: IOrganizationResult;
  tour?: ITourResult;
  customer?: IIndividualResult;
  material?: IMaterialResult;
  invoiceReceiver?: IIndividualResult | IOrganizationResult;
  salesDistributionChannel?: IOrganizationResult;
  salesDivision?: IOrganizationResult;
  discountType?: ISimpleModelResult;
  taxClassCondition?: ITaxClassConditionResult;
  taxCountry?: IGeographicBoundaryResult;
  roomType?: IAccommodationRoomTypeResult;
  bedType?: IAccommodationBedTypeResult;
  bedStatus?: ISimpleModelResult;
  foodType?: ISimpleModelResult;
  buffetType?: ISimpleModelResult;
  kitchenType?: ISimpleModelResult;
  visaType?: IVisaTypeResult;
  transportationType?: ISimpleModelResult;
  ageGroup?: IAgeGroupResult;
  concept?: IConceptResult;
  condition?: IPricingConditionResult;
};

export type ITaxClassConditionResult = {
  id: number;
  code: string;
  name: string;
  description: string;
  status: boolean;
  pricingConditionType: IPricingConditionTypeResult;
  country: IGeographicBoundaryResult;
};

export type ICampaignResult = {
  id: number;
  code: string;
  name: string;
  description: string;
  type: ISimpleModelResult;
  startDate: string;
  endDate: string;
  status: boolean;
  pricingConditionType: IPricingConditionTypeResult;
  pricingAccessSequenceItem: IPricingAccessSequenceItemResult;
  minimumProductCount: number;
  minimumSalesPrice: number;
  paymentPercent: number;
  combinable: boolean;
  combinableCampaign: ICampaignResult;
  calculationBasedOnPNR: boolean;
  conditionRelation: CamapignConjunctionType;
  productGroup: IProductGroupResult;
  paymentMethod: ISimpleModelResult;
  applyInBag?: boolean;
  autoApply?: boolean;
  company?: IOrganizationResult;
};

export type IPricingConditionTypeResult = {
  id: number;
  name: string;
  code: string;
  conditionClass: ConditionClassType;
  sequence: IPricingAccessSequenceResult;
  unit: UnitType;
  rounding: RoundingType;
  applyOn: ApplyOnType;
  revertable: boolean;
  updateable: boolean;
  status: boolean;
  nameLabel?: string;
};

export type IOrganizationResult = {
  id: number;
  code: string;
  description: string;
  name: string;
  taxCity: IGeographicBoundaryResult;
  taxCountry: IGeographicBoundaryResult;
  taxNo: string;
  taxOffice: string;
  logo?: IDocumentFile;
  customerCode?: string;
};

export type IDocumentFile = {
  createdBy: string;
  createdDate: string;
  description: string;
  fileExtension: string;
  filePath: string;
  fileSize: number;
  id: number;
  lastModifiedBy: string;
  lastModifiedDate: string;
  originalFileName: string;
  firstName: string;
  lastName: string;
  passportNumber: string;
  salesIndividulaId: number;
  salesGroupCode: number;
  individualDocumentTypeName: string;
};
export type IMaterialResult = {
  id: number;
  name: string;
  code: string;
  description?: string;
  group: IReferenceCode;
  type: IReferenceCode;
  startDate: Date;
  endDate: Date;
  state: IReferenceCode;
  measureUnit: IReferenceCode;
  roomType?: IAccommodationRoomTypeResult;
  bedType?: IAccommodationBedTypeResult;
  foodType?: ISimpleModelResult;
  buffetType?: ISimpleModelResult;
  kitchenType?: ISimpleModelResult;
  ageGroup?: IAgeGroupResult;
  bedStatus?: ISimpleModelResult;
  supplyType?: SupplyType;
  accommodationCity?: IGeographicBoundaryResult;
  transportationType?: ISimpleModelResult;
  transportationDeparture?: IGeographicBoundaryResult;
  transportationArrival?: IGeographicBoundaryResult;
  materialItemTypeGroup?: ISimpleModelResult;
  visaType?: IVisaTypeResult;
  configurationType: ConfigurationType;
  codeNameLabel?: string;
};

export type IAccommodationRoomTypeResult = {
  id?: number;
  code?: string;
  name?: string;
  description?: string;
  boundRoom?: number;
  status?: boolean;
};

export type IAccommodationBedTypeResult = {
  id?: number;
  code?: string;
  name?: string;
  description?: string;
  numberOfPerson?: number;
  icon?: string;
  status?: boolean;
};

export type ISimpleModelResult = {
  id: number;
  name: string;
  code: string;
  description?: string;
};

export type IAgeGroupResult = {
  id: number;
  code: string;
  name: string;
  startAgeInMonth: number;
  endAgeInMonth: number;
  description: string;
  status: boolean;
  capacityNotIncluded: boolean;
};

export type IVisaTypeResult = {
  id: number;
  code: string;
  name: string;
  country: IGeographicBoundaryResult;
  description?: string;
  status: boolean;
  passportValidityDays?: number;
  residencyValidityDays?: number;
  orderPriority?: number;
};

export type User = {
  id?: number;
  login?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  activated?: boolean;
  isChecked?: boolean;
  langKey?: string;
  authorities?: string[];
  password?: string;
  imageUrl?: string;
};

export type ITourResult = {
  id: number;
  name?: string;
  branchList: IReferenceCodeBaseLOV[];
  capacity?: number;
  company?: IReferenceCodeBaseLOV;
  concept?: IConceptResult;
  country?: IReferenceCodeBaseLOV;
  description?: string;
  endDate: Date;
  seasons: ISimpleModelResult[];
  startDate: Date;
  status: boolean;
  term?: ITermResult;
  tourClass?: {
    id: number;
    name: string;
    day: number;
    imgUrl?: string;
  };
  plan: ITourPlanResult;
  tourCode: string;
  registered?: number;
  salesArea: ISalesAreaResult;
  salesOfficeArea: ISalesOfficeAreaResult;
  lastRegistrationDate: string;
};

export type IDocumentItemTypeResult = {
  id: number;
  code: string;
  name: string;
  description: string;
  status: boolean;
  invoiceRelationship: InvoiceType;
  pricingCheck: PriceType;
  settlementControl?: boolean;
  goodsReceipt?: boolean;
  orderAmount?: number;
};

export type IConceptResult = {
  id?: number;
  code?: string;
  status?: boolean;
  name?: string;
  description?: string;
  colorCode?: string;
};

export type ITermResult = {
  id?: number;
  code?: string;
  name?: string;
  description?: string;
  startDate?: Date;
  endDate?: Date;
  status?: boolean;
};

export type ITourPlanResult = {
  id: number;
  name: string;
  description: string;
  status: boolean;
  concept: IConceptResult;
  tourClass: ITourClassResult;
};

export type ITourClassResult = {
  id: number;
  name: string;
  description: string;
  productGroup: IProductGroupResult;
  day: number;
  status: boolean;
  imgUrl: string;
  accountingCode: string;
};

export type IProductGroupResult = {
  id?: number;
  code?: string;
  status?: boolean;
  name?: string;
  description?: string;
};

export type ISalesAreaResult = {
  id: number;
  organization: IOrganizationResult;
  distributionChannel: ISalesDistributionChannelResult;
  salesDivision: ISimpleModelResult;
  status: boolean;
};

export type ISalesDistributionChannelResult = {
  id: number;
  code: string;
  name: string;
  description: string;
  status: boolean;
};

export type ISalesOfficeAreaResult = {
  id: number;
  salesArea: ISalesAreaResult;
  salesOffice: IOrganizationResult;
};

export type IPricingAccessSequenceItemResult = {
  id: number;
  sequence: IPricingAccessSequenceResult;
  order: number;
  fields: string[];
  stopProceeding: boolean;
  status: boolean;
  nameLabel?: string;
};

export type IPricingAccessSequenceResult = {
  id: number;
  name: string;
  description: string;
};

export type IRoomGroupCustomer = {
  icon: string;
  name: string;
  lastName: string;
};
export interface IAccommodationInfo {
  id: number;
  accommodationStayId?: number;
  purchasingRequisitionItemId?: number;
  salesOrderItemId?: number;
  salesOrderId?: number;
  salesOffice?: string;
  ageGroup?: string;
  city: string;
  startDate: string;
  endDate: string;
  stayNight: number;
  accommodationStayName?: string;
  accommodationStayImagePath?: string;
  accommodationStayStar?: string;
  latitude?: string;
  longitude?: string;
  roomGroupId?: number;
  roomType?: string;
  bedType?: string;
  bedStatus?: string;
  roomNumber?: number;
  roomGroupCustomer?: IRoomGroupCustomer[];
}
export interface IPassportInfo {
  id: number;
  purchasingRequisitionItem: IPurchasingRequisitionItem;
  passport: IPassport;
}

export type IPurchasingRequisitionItem = {
  material: IMaterial;
};

export type IMaterial = {
  group: IGroup;
};

export type IGroup = {
  name: string;
};

export type IPassport = {
  id: number;
  startDate: string;
  endDate: string;
  mrz: string;
  issuedBy: string;
  number: string;
  individual: Individual;
  country: Country;
  type: Type;
  birthPlace: string;
  birthDate: string;
  citizenshipNumber: string;
  nationality: Nationality;
  firstName: string;
  lastName: string;
  scannedAt: string;
};

export type ISalesIndividualPassport = {
  itemId: number;
  itemNumber: number;
  documentNumber: number;
  visaType: string;
  gender: string;
  individualLastName: string;
  individualFirstName: string;
  passportId: number;
  passportLastName: string;
  passportFirstName: string;
  passportNationalityCode: string;
  passportNationality: string;
  passportStartDate: string;
  passportEndDate: string;
  passportNumber: string;
  passportMRZ: string;
  passportIssuedBy: string;
  passportCitizenshipNumber: string;
  passportScannedAt: string;
  passportScannerPlatform: string;
  passportBirthDate: string;
  passportBirthPlace: string;
  passportPassportScannerDeviceDetail: string;
  passportPassportScannedType: string;
  passportCreatedBy: string;
  passportCreatedDate: string;
  passportLastModifiedBy: string;
  passportLastModifiedDate: string;
  passportCountryCode: string;
  passportCountry: string;
  passportType: string;
  biometricPhotoPath: string;
  passportFrontPhotoPath: string;
  biometricPhotoFullPath: string;
  passportFrontPhotoFullPath: string;
};

export type Individual = {
  id: number;
  gender: string;
};

export type Country = {
  name: string;
};

export type Type = {
  name: string;
};

export type Nationality = {
  name: string;
};

export interface ITransportationInfos {
  departure: string;
  salesIndividualId: number;
  departureDate: string;
  arrivalDate: string;
  arrivalTime?: string;
  departureTime?: string;
  durationInMinutes: string;
  transportationType: string;
  direction: string;
  salesOrderId: number;
  flightPnr?: string;
  flightNumber?: string;
  transportationCompanyLogo?: string;
  transportationCompanyName?: string;
  transportationFullPathCompanyLogo?: string;
  arrival: string;
  salesOrderItemId: number;
  id: number;
  relatedTransportationAddable: boolean;
}

export interface ITourGuideInfoResult {
  phoneNumber: string;
  email: string;
  tourGroupName: string;
  guideType: string;
  fullName: string;
}

export interface ITourCustomerCount {
  capacity: number;
  fulfilledCapacity: number;
}

export interface IGuideSalesIndividualTourGroupInfo {
  tourGroupId: number;
  tourGroupName: string;
  guideInfo: {
    fullName: string;
    position: string;
  }[];
  groupInfo: {
    settled: number;
    ageGroupName: number;
    gender: GenderType;
    startAgeInMonth: number;
  }[];
}

export interface IGuideSalesIndividualTourAccommodationInfo {
  id: number;
  accommodationStayId: number;
  stayName: string;
  stayFullPathImage: string;
  city: string;
  starNumber: number;
  startDate: string;
  endDate: string;
  stayNight: number;
  totalPax: number;
  unSettledPax: number;
  tourPlanId: number;
  latitude: string;
  longitude: string;
  roomInfos: {
    roomType: string;
    bedTypeCode: string;
    bedType: string;
    settled: number;
    unSettled: number;
  }[];
}

export interface ITourCustomerResult {
  firstName: string;
  lastName: string;
  loyaltyNumber: string;
  pnr: string;
  domesticArrival: string;
  domesticReturn: string;
  salesOrderGroup: string;
  salesIndividualId: number;
  tourGroup: string;
  orderNumber: string;
  customerNumber: string;
  birthDate: string;
}

export interface ITourFlightResult {
  arrival: string;
  arrivalName: string;
  arrivalDate: string;
  arrivalTime: string;
  departure: string;
  departureName: string;
  departureDate: string;
  departureTime: string;
  durationInMinutes: string;
  direction: string;
  number: string;
  tourPlanId: number;
  transportationCompanyName?: string;
  transportationCompanyLogo?: string;
  transportationFullPathCompanyLogo?: string;
}

export interface IGeneralCustomerList {
  fullName: string;
  position: string;
  bedTypeCode: string;
  age: number;
  salesOffice: string;
  salesOrderGroup: string;
  city: string;
  district: string;
  disability: string;
  tourGroup: string;
  pnr: string;
  gender: string;
  birthDate: string;
  startAgeInMonth: number;
  salesOrderNumber: string;
  phoneNumber: string;
  id: number;
  salesIndividualId: number;
}

export interface IReferenceTypeId {
  id: number;
  name: string;
  typeId: number;
  code: string;
  status: boolean;
  description: string;
  orderPriority: number;
  parent: IReferenceTypeId;
}

export interface ITransportationRequisition {
  id: number;
  name: string;
  typeId: number;
  code: string;
  status: boolean;
  description: string;
  orderPriority: number;
  parent: IReferenceTypeId;
}

export type IGuideTourHealthDisability = {
  id?: number;
  customer?: {
    id: number;
    fullName: string;
    salesIndividualId: string | number;
  };
  guideSalesIndividualId?: string | number;
  salesIndividualId?: string | number;
  referenceTypeId?: number;
};

export type IGuideTourMedicalReport = {
  id?: number;
  guideSalesIndividualId?: string | number;
  salesIndividualId?: string | number;
  file?: File;
};

export type IGuideTourCustomerDisability = {
  disabilityId: number;
  salesIndividualDisabilityId: number;
  customerFirstName: string;
  customerLastName: string;
  disability: string;
  salesIndividualId: number;
};

export type IGuideTourMedicalAppointment = {
  id: number;
  date: string;
  time: string;
  responsible: string;
  type: string;
  hospital: string;
  description: string;
  state: string;
  createdBy: string;
  city?: string;
  firstName: string;
  lastName: string;
  accommodationStayName: string;
  hospitalName: string;
  salesIndividualId: number;
  salesIndividual: {
    id: string;
    customer: {
      firstName: string;
      lastName: string;
    };
  };
};

export interface ICustomerVisaList {
  customerFullName: string;
  passportNumber: string;
  visaNo: string;
  state: string;
  salesGroupName: string;
  salesOfficeName: string;
  tourGroupName: string;
  phoneNumber?: string;
  assignmentId: number;
  visaDocFileFullPath: string;
}

export interface ICustomerPaymentStateList {
  customerFullName: string;
  totalAmount: number;
  paymentTotal: number;
  balanceAmount: number;
  discountAmount: number;
  bedType: string;
  salesOffice: string;
  salesOrderGroup: string;
  salesIndividualStatus: boolean;
  salesOrderState: string;
  fullNumber: string;
  tourGroupName: string;
  salesIndividualId: number;
}

export interface IGuideTourAccommodationGroupSettledList {
  position: string;
  pnr: string;
  ageGroup: string;
  bedType: string;
  roomNumber?: string;
  customerFullName: string;
  roomGroupId: number;
  roomType: string;
  bedStatus: string;
  checkInDate: string;
  checkOutDate: string;
  nightCount: number;
  relatedGroupId: number;
  salesOrderOffice: string;
  salesOrderGroup: string;
  maxCapacity: number;
  id: number;
  age: number;
  startAgeInMonth: number;
  gender: string;
}

export interface ISalesIndividualVisaInfo {
  itemId: number;
  documentNumber: number;
  itemNumber: number;
  visaType: string;
  visaGroup: string;
  visaCompany: string;
  country: string;
  consulate: string;
  individualId: number;
  salesIndividualId: number;
  mofaGroupAssignmentState: string;
  mofaGroupAssignmentStateDescription: string;
  mutamarNo: string;
  mofaNo: string;
  moiNo: string;
  appNo: string;
  insuranceNumber: string;
  visaNo: string;
  visaDate: string;
  visaStartDate: string;
  visaExpireDate: string;
  mofaDate: string;
  visaDocPath: string;
  visaDocFileFullPath: string;
  insuranceDocPath: string;
  insuranceDocFileFullPath: string;
  mofaAgent: string;
  gdsId: string;
}

export interface IOrderInfoSalesOrderItem {
  id: number;
  material: {
    name: string;
    code: string;
  };
  quantity: number;
  state: string;
  invoiceState: string;
  pricing: {
    amount: number;
    currency: {
      code: string;
    };
  };
  discounts: {
    amount: string;
    discountType: {
      name: string;
    };
    campaign: {
      name: string;
    };
    currency: {
      code: string;
    };
  }[];
}

export interface IOrderInfoSalesOrderAccepter {
  id: number;
  party: {
    id: number;
    spec: string;
    partyInfo: {
      firstName: string;
      lastName: string;
      name: string;
      taxNo: string;
      taxOffice: {
        name: string;
      };
    };
  };
  address: {
    fullAddress: string;
  };
  phoneNumber: {
    countryCode: string;
    phoneNumber: string;
    fullNumber: string;
  };
  electronicAddress: {
    electronicAddress: string;
  };
  externalContactMedium: {
    owner: string;
    phoneNumber: {
      countryCode: string;
      phoneNumber: string;
      fullNumber: string;
    };
    postalAddress: {
      fullAddress: string;
    };
    electronicAddress: {
      electronicAddress: string;
      id: number;
    };
  };
  role: {
    name: string;
    code: string;
  };
}

export interface IOrderInfoPaymentTransaction {
  salesOrderCalculatedPrice: {
    balance: number;
    tax: number;
  };
  paymentTransactions: {
    paymentType: string;
    currency: {
      code: string;
    };
    amount: string;
    date: string;
    description: string;
    erpTransactionNumber: string;
  }[];
}

export interface IOnlineTourSearchPropertyResult {
  id: number;
  searchText: string;
  type: string;
}

export interface IOnlineTourSearchResult {
  id: number;
  name: string;
  code: string;
  images: {
    path: string;
    videoPath: string;
    favorite: boolean;
  }[];
  departureDeparture: string;
  departureDepartureCity: string;
  departureArrival: string;
  departureArrivalCity: string;
  arrivalArrival: string;
  arrivalArrivalCity: string;
  arrivalDeparture: string;
  arrivalDepartureCity: string;
  departureDate: string;
  arrivalDate: string;
  dayCount: number;
  availableCapacity: number | null;
  adultPrice: number;
  currencyCode: string;
  flightCompanyInfo: {
    logo: string;
    name: string;
  }[];
  remainingDay: number | null;
  totalCount: number;
  parameters: { parameter: TourParameter; value: string }[];
}

export type TourParameter =
  | 'WHATSAPP_PHONE_NUMBER'
  | 'SALES_PERMISSION'
  | 'RESERVATION_PERMISSION'
  | 'CONTACT_ME';

export interface IOnlineTourPresentationResult {
  name: string;
  description: string;
}

export interface IOnlineTourPriceResult {
  id: number;
  bedType: string;
  bedTypeDescription: string;
  ageGroup: string;
  startAgeInMonth: number;
  price: number;
  currencyCode: string;
  bedStatus: string;
  bedTypeCapacity: number;
}

export interface IOnlineTourHotelResult {
  id: number;
  accommodationStayId: number;
  stayName: string;
  star: number;
  startDate: string;
  endDate: string;
  day: number;
  stayNight: number;
  longitude: number;
  latitude: number;
  webSite: string;
  image: string;
  cityName: string;
}

export interface IOnlineTourTransportationResult {
  id: number;
  flightNumber: string;
  company: string;
  companyLogo: string;
  departure: string;
  arrival: string;
  departureDate: string;
  arrivalDate: string;
  departureTime: string;
  arrivalTime: string;
  direction: string;
}

export interface IOnlinePreferredTourCategory {
  id: number;
  name: string;
  description?: string;
  imagePath?: string;
  iconPath?: string;
  price?: number;
  order: number;
  currencyCode?: string;
}

export type IMemberSalesOrderInfoResult = {
  bedType: string;
  bedTypeId: number;
  customerFirstName: string;
  customerLastName: string;
  currency: string;
  debtAmount: number;
  discountAmount: number;
  lastRoomChangeDate: string;
  lastSalesCancellationDate: string;
  lastTransferDate: string;
  mainSalesOrderItemId: number;
  orderCompanyId: number;
  orderDate: string;
  orderNumber: string;
  positionTypeCode: string;
  pnr: string;
  salesIndividualId: number;
  tourEndDate: string;
  tourId: number;
  tourInfo: string;
  tourStartDate: string;
  totalAmount: number;
};

export interface ICurrency {
  code: string;
}

export interface IBedType {
  name: string;
  id: number;
}
export interface IPrice {
  id: number;
  bedStatus: {
    name: string;
  };
  concept: {
    name: string;
  };
  bedType: IBedType;
  currency: ICurrency;
  name: string;
  amount: number;
}
export interface ITour {
  id: number;
  name: string;
  startDate: string;
}
export interface ITourPrice {
  calculatedPrice: number;
  tour: ITour;
  price: IPrice;
}

export type ITourPlanActivity = {
  tourInfo: {
    id: number;
    tourCode: string;
    tourCapacity: number;
  }[];
  dateInfo: {
    date: string;
    trips: {
      id: number;
      plans: {
        id: number;
        accommodationCityName: null;
        departureName: string[];
        arrivalName: string[];
        time: string;
        transportationNumber: string;
        vehicleTypeCode: string;
        vehicleName: string;
        vehicleTypeName: string;
        vehicleCount: number;
        tourCode: string;
        activityPlan: false;
        transportationCompany: string;
      }[];
    }[];
  }[];
};

export type ITourDocumentInfo = {
  tourId: number;
  requiredDocumentType: {
    name: string;
    description: string;
    id: number;
    code: string;
    status: boolean;
  };
  requiredCount: number;
  count: number;
};

export type ITourVisaInfo = {
  requiredCount: number;
  completedCount: number;
};

export type ITourPaymentInfo = {
  notFullAmountPaymentCount: number;
  fullAmountPaymentCount: number;
  overPaymentCount: number;
  notPaymentCount: number;
};

export type IEarlyPaymentCalculationResult = {
  duePaymentDate: string;
  amount: number;
  paymentType: PaymentType;
  currency: string;
  pricingUnit: PricingUnitEnum;
};

export type ICitizenshipResult = {
  id: number;
  fromDate: string;
  toDate: string;
  description: string;
  status: boolean;
  citizenshipNumber: string;
  country: IGeographicBoundaryResult;
  firstName: string;
  lastName: string;
  maritalStatus: IReferenceCode;
  militaryStatus: IReferenceCode;
};

export type ICitizenshipInput = {
  citizenshipNumber: string;
  countryId: number;
  description?: string;
  firstName: string;
  fromDate: string;
  lastName: string;
  maritalStatusId?: number;
  militaryStatusId?: number;
  status?: boolean;
  toDate: string;
};

export type OrderDetailTabTypes = 'tourInfoTab' | 'orderInfosTab' | 'guideTab';

export type OrderDetailTabsType =
  | 'presentationTab'
  | 'accommodationTab'
  | 'transportationTab'
  | 'myPassportTab'
  | 'myVisasTab'
  | 'myGuideTab'
  | 'guideTab'
  | 'orderInfosTab';

export type OnlineTourDetailTabTypes =
  | 'presentation'
  | 'prices'
  | 'hotels'
  | 'transportation'
  | 'tourPlan';

export enum PaymentType {
  CASH = 'CASH',
  TRANSFER = 'TRANSFER',
  CREDITCARD = 'CREDITCARD',
  VIREMENT = 'VIREMENT',
  LOYALIT_POINT = 'LOYALIT_POINT',
}

export enum PricingUnitEnum {
  PERCENT = 'PERCENT',
  AMOUNT = 'AMOUNT',
}

export interface ElectronicAddress {
  electronicAddress: string;
  typeId: number;
}

export interface PhoneNumber {
  areaCode: string | null;
  countryCode: string;
  phoneNumber: string;
  typeId: number;
}

export interface ICallBackFormInput {
  customerFullName: string;
  description: string;
  desiredContactDate?: string;
  desiredContactTime?: string;
  electronicAddress: ElectronicAddress;
  phoneNumber: PhoneNumber;
  personCount: number;
  tourId: number;
}

export type IPartyContactInfoInput = {
  electronicAddress?: {
    contactMediumInfo: {
      electronicAddress: string;
      typeId: number;
    };
    description?: string;
    fromDate: string;
    toDate: string;
    usageTypeId: number;
  };
  phoneNumber?: {
    contactMediumInfo: {
      areaCode?: string;
      countryCode: string;
      phoneNumber: string;
      typeId: number;
    };
    description?: string;
    fromDate: string;
    toDate: string;
    usageTypeId: number;
  };
  postalAddress?: {
    contactMediumInfo: {
      doorNumber?: string;
      fullAddress?: string;
      geographicBoundaryId: number;
      postCode?: string;
      streetAddress1: string;
      streetAddress3?: string;
      typeId: number;
    };
    description?: string;
    fromDate: string;
    toDate: string;
    usageTypeId: number;
  };
};

export type IPartyContactInfoResult = {
  electronicAddress: Array<{
    contactMediumInfo: {
      electronicAddress: string;
      id: number;
      type: { code: string; id: number; name: string };
    };
    default: boolean;
    description: string;
    fromDate: string;
    id: number;
    toDate: string;
    usageType: { code: string; id: number; name: string };
  }>;
  phoneNumber: Array<{
    contactMediumInfo: {
      areaCode: string;
      countryCode: string;
      fullNumber: string;
      id: number;
      phoneNumber: string;
      type: { code: string; id: number; name: string };
    };
    default: boolean;
    description: string;
    fromDate: string;
    id: number;
    toDate: string;
    usageType: { code: string; id: number; name: string };
  }>;
  postalAddress: Array<{
    contactMediumInfo: {
      id: number;
      type: { code: string; id: number; name: string };
      doorNumber: string;
      fullAddress: string;
      geographicBoundary: IGeographicBoundaryResult;
    };
    default: boolean;
    description: string;
    fromDate: string;
    id: number;
    toDate: string;
    usageType: { code: string; id: number; name: string };
  }>;
};

export type ISalesOrderInput = {
  addressId: number;
  electronicAddressId: number;
  partyId: number;
  phoneNumberId: number;
  pricingId: number;
  mahramId?: number;
  alternativeArrivalId?: number;
  alternativeDepartureId?: number;
  mahramTypeId?: number;
  referenceInfo?: string;
  relatedTransportationDepartureId?: number;
  relatedTransportationReturnId?: number;
};

export type ISalesOrderResult = {
  id: number;
  status: boolean;
  customerDescription: string | null;
  createdBy: string;
  createdDate: string;
  customer: IIndividualResult;
  salesGroup: {
    id: number;
    code: string;
    tour: {
      name: string;
      description: string;
      id: number;
      startDate: string;
      endDate: string;
      lastRegistrationDate: string;
      capacity: number;
      status: boolean;
      tourCode: string;
      country: IGeographicBoundaryResult;
      company: {
        id: number;
        code: string;
        name: string;
      };
      term: {
        name: string;
        description: string | null;
        id: number;
        code: string;
        startDate: string;
        endDate: string;
        status: boolean;
      };
      tourState: string;
      productType: {
        name: string;
        description: string | null;
        id: number;
        code: string;
        status: boolean;
        categories: {
          name: string;
          description: string;
          id: number;
          code: number;
          status: boolean;
          icon: string | null;
        }[];
        priceFilter: string;
        priceColumn: string;
        priceRow: string;
      };
      dayCount: number;
      priceFilter: string;
      priceColumn: string;
      priceRow: string;
      seasons: any | null;
    };
  };
  roomType: IAccommodationRoomTypeResult;
  bedType: IAccommodationBedTypeResult;
  citizenship: {
    id: number;
    fromDate: string;
    toDate: string;
    description: string | null;
    status: boolean | null;
    citizenshipNumber: string | null;
    country: IGeographicBoundaryResult;
    party: IIndividualResult;
    firstName: string;
    lastName: string;
    maritalStatus: IReferenceCode;
    militaryStatus: IReferenceCode;
  };
  order: {
    id: number;
    description: string | null;
    cancelledDate: string | null;
    documentNumber: string | null;
    documentClass: string | null;
    documentType: string | null;
    salesOfficeArea: {
      id: number | null;
      salesOffice: IOrganizationResult | null;
      salesArea: {
        id: number | null;
        status: boolean | null;
        organization: IOrganizationResult | null;
        distributionChannel: ISalesDistributionChannelResult | null;
        salesDivision: ISimpleModelResult | null;
      };
    };
    salesGroup: any | null;
    createdBy: string | null;
    createdDate: string;
    owner: any | null;
    orderDate: string | null;
    company: any | null;
    state: string | null;
    cancelReason: any | null;
    invoiceState: OrderInvoiceStateType | null;
    currency: any | null;
  };
  ageGroup: IAgeGroupResult;
  education: any | null;
  occupation: any | null;
  requisition: {
    id: number;
    description: string | null;
    documentNumber: string | null;
    lastestVersion: string | null;
    company: any | null;
    documentClass: string | null;
    state: string | null;
    cancelReason: any | null;
    createdDate: string;
    createdBy: string | null;
  };
  positionType: ISimpleModelResult;
  bedStatus: ISimpleModelResult;
  foodType: any | null;
  kitchenType: any | null;
  buffetType: any | null;
  concept: IConceptResult;
  productType: {
    name: string;
    description: string | null;
    id: number;
    code: string;
    status: boolean;
    categories: {
      name: string;
      description: string;
      id: number;
      code: number;
      status: boolean;
      icon: string | null;
    }[];
    priceFilter: string;
    priceColumn: string;
    priceRow: string;
  };
  gender: GenderType;
  mainSalesOrderItem: {
    id: number;
    description: string | null;
    taxRate: number;
    parent: ISalesOrderItemResult | null;
    itemNumber: number;
    quantity: number;
    deliveryDate: string;
    startDate: string | null;
    endDate: string | null;
    plannedInvoiceDate: string;
    cancelledDate: string | null;
    state: OrderStateType;
    invoiceState: OrderInvoiceStateType | null;
    material: IMaterialResult;
    productionSite: IOrganizationResult;
    warehouse: IOrganizationResult;
    order: {
      id: number;
      description: string | null;
      documentNumber: string | null;
      cancelledDate: string | null;
      orderDate: string | null;
      documentClass: string | null;
      documentType: string | null;
      organization: any | null;
      office: any | null;
      distributionChannel: any | null;
      division: any | null;
      salesGroup: any | null;
      company: any | null;
      party: any | null;
      cancelReason: any | null;
      state: string | null;
      invoiceState: OrderInvoiceStateType | null;
      currency: any | null;
      salesOrderAccepters: any | null;
    };
    cancelReason: any | null;
    documentItemType: IDocumentItemTypeResult;
  };
  accommodationSalesOrderItem: ISalesOrderItemResult;
  transportationSalesOrderItem: ISalesOrderItemResult;
  price: {
    subTotal: number;
    discount: number;
    tax: number;
    balance: number;
    currency: ICurrency;
  };
  salesOrderAccepters: IOrderInfoSalesOrderAccepter[];
};

export const getPaymentTypeLabel = (paymentType: string): string => {
  const enumValue = paymentType as PaymentType;
  switch (enumValue) {
    case PaymentType.CASH:
      return t('orderInfos.cash');
    case PaymentType.TRANSFER:
      return t('orderInfos.transfer');
    case PaymentType.CREDITCARD:
      return t('orderInfos.creditCard');
    case PaymentType.VIREMENT:
      return t('orderInfos.virement');
    case PaymentType.LOYALIT_POINT:
      return t('orderInfos.loyalitPoint');
    default:
      return paymentType;
  }
};
